
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  Button, ListboxStyled, ListboxStyledButton, ListboxStyledOption, TailwindSpinner,
} from 'src/components/common';
import { ListboxWrapper } from '@components/common/Forms';
import { ListboxStyledOptions } from '@components/common/Listbox/ListboxOptions';
import { CheckIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'next-i18next';

type BillingType = 'individual' | 'company';

type BillingSelectorProps = {
  billingData: ApiSchema<'BillingDataRead'>[];
  setBillingType: (type: BillingType) => void;
  selectedBillingInfo: ApiSchema<'BillingDataRead'> | undefined;
  selectBillingInfo: (item: ApiSchema<'BillingDataRead'>) => void | undefined;
  className?: string;
  isLoadingBillingType?: boolean;
};

export const BillingDataSelector = ({
  billingData,
  setBillingType,
  selectedBillingInfo,
  selectBillingInfo,
  isLoadingBillingType,
  className,
}: BillingSelectorProps) => {
  const { t } = useTranslation('common');
  const shouldRenderBillingData = (Array.isArray(billingData) && billingData.length > 0) && !isLoadingBillingType;

  return (
    <div className={`${className} p-6`}>
      <div className="my-2 font-bold">{t('global.label.choose.payment.invoice.type')}</div>

      <div className="flex items-center justify-between gap-3">
        <ListboxWrapper
          fieldName="userType"
          data={[
            { label: t('global.label.choose.payment.invoice.type.company'), value: 'company' },
            { label: t('global.label.choose.payment.invoice.type.person'), value: 'individual' },
          ]}
          defaultValueKey="value"
          labelKey="label"
          renderListbox={({ lsData, selected, setSelected }) => {
            const selectedItem = selected as { value: string; label: string };
            const buttonLabel = selectedItem.label;

            return (
              <ListboxStyled className="relative w-full" by="value" value={selected}>
                <ListboxStyledButton
                  as={Button}
                  styling="none"
                  className="flex w-full items-center justify-between gap-2 rounded border border-input px-4 py-2"
                >
                  {buttonLabel}
                  <ChevronDownIcon className="-mr-2 size-5 shrink-0 text-ink/80" />
                </ListboxStyledButton>

                <ListboxStyledOptions
                  className="scrollbar-thin mt-0.5 max-h-40 min-w-full overflow-y-auto py-1"
                  animation="slideDown"
                  position="right"
                >
                  {lsData.map((field) => (
                    <ListboxStyledOption
                      key={field.value}
                      value={field}
                      className="flex cursor-pointer items-center gap-1.5 py-1.5 pl-2.5 pr-3.5 text-sm hover:bg-secondary hover:text-surface"
                      onClick={() => {
                        setSelected(field);
                        setBillingType(field.value as 'individual' | 'company');
                      }}
                    >
                      {field.label}
                    </ListboxStyledOption>
                  ))}
                </ListboxStyledOptions>
              </ListboxStyled>
            );
          }}
        />
      </div>

      {isLoadingBillingType && (
      <div className="flex items-center justify-center py-10">
        <TailwindSpinner className="size-10 text-ink-light" />
      </div>
      )}


      {shouldRenderBillingData
        && <div className="mb-1 mt-5 text-xs font-thin italic">{t('global.label.choose.payment.invoice.hint')}</div>}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        {shouldRenderBillingData
          && billingData.map((item) => {
            const isSelected = JSON.stringify(item) === JSON.stringify(selectedBillingInfo);

            return (
              <div
                key={JSON.stringify(item)}
                role="presentation"
                onClick={() => selectBillingInfo(item)}
                className={`${isSelected ? 'bg-secondary text-white' : ''} relative mb-3 cursor-pointer rounded border border-dashed p-3 text-xs transition hover:bg-secondary hover:text-surface`}
              >
                {isSelected && (
                  <div
                    className="absolute -right-2 -top-2 flex size-7 items-center justify-center rounded-full border border-secondary bg-surface"
                  >
                    <CheckIcon className="size-7 text-secondary" />
                  </div>
                )}

                {item.companyName && <div className="font-bold">{item.companyName}</div>}

                {(item.identifier || item.registrationNumber) && (
                  <div>
                    {item.identifier}
                    {item.identifier && item.registrationNumber && ' / '}
                    {item.registrationNumber}
                  </div>
                )}

                {item.bankName && <div>{item.bankName}</div>}
                {item.bankAccount && <div>{item.bankAccount}</div>}
                {item.city && <div>{item.city}</div>}
                {item.address && <div>{item.address}</div>}

                {item.phone?.number && item.phone?.country?.countryCode && (
                  <div>
                    +
                    {item.phone.country.countryCode}
                    {' '}
                    {item.phone.number}
                  </div>
                )}

                {item.mailingEmail && <div>{item.mailingEmail}</div>}
                {item.mailingAddress && <div>{item.mailingAddress}</div>}
              </div>
            );
          })}
      </div>
    </div>
  );
};
